"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Sun,
  Moon,
  ArrowRight,
  Github,
  Link as LinkIcon,
  SlidersHorizontal,
  ShieldCheck,
  Cpu,
  Workflow,
} from "lucide-react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { useTheme } from "./src/ThemeContext.jsx";

/**
 * Projects — Matches Hire Me page UI/UX
 * - Same navbar, theme system, 3D tilt, magnetic buttons, parallax accents
 * - Unified buttons (indigo→cyan gradient) like the rest of the site
 */
export default function ProjectsPage() {
  const { darkMode, toggleTheme } = useTheme();

  return (
    <div
      className={
        darkMode
          ? "min-h-screen bg-[#0a0c10] text-white"
          : "min-h-screen bg-white text-gray-900"
      }
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white"
      >
        Skip to content
      </a>

      {/* NAV — identical to homepage / Hire Me */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div
            className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${
              darkMode
                ? "bg-white/5 border border-white/10"
                : "bg-white/80 border border-white/90"
            }`}
          >
            <a href="/" className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${
                  darkMode
                    ? "bg-gradient-to-br from-indigo-500 to-cyan-400"
                    : "bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow"
                }`}
              >
                ST
              </div>
              <div className="leading-tight">
                <div
                  className={`text-sm font-semibold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Sikandar Tareen
                </div>
                <div
                  className={`text-[11px] ${
                    darkMode ? "text-white/60" : "text-gray-600"
                  }`}
                >
                  Agentic AI Engineer
                </div>
              </div>
            </a>
            <div
              className={`hidden md:flex items-center gap-6 text-sm ${
                darkMode ? "text-white/70" : "text-gray-700"
              }`}
            >
              <a
                href="/"
                className={`${darkMode ? "hover:text-white" : "hover:text-black"}`}
              >
                Home
              </a>
              <a
                href="/projects"
                className={`${darkMode ? "text-white" : "text-black"} font-medium`}
              >
                Projects
              </a>
              <a
                href="/blog"
                className={`${darkMode ? "hover:text-white" : "hover:text-black"}`}
              >
                Blog
              </a>
              <a
                href="/about-me"
                className={`${darkMode ? "hover:text-white" : "hover:text-black"}`}
              >
                About
              </a>

              <ButtonPrimary darkMode={darkMode} href="/contact" small>
                <Sparkles size={14} /> Contact Me
              </ButtonPrimary>
            </div>
          </div>
        </nav>
      </header>

      {/* Parallax accents */}
      <ParallaxField darkMode={darkMode} />

      <main
        id="main"
        className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-10 [perspective:1200px]"
      >
        {/* Hero */}
        <section className="relative overflow-visible">
          <div
            className={`absolute -top-24 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl ${
              darkMode ? "bg-indigo-600/20" : "bg-indigo-200/60"
            }`}
          />
          <div
            className={`absolute -bottom-32 -right-16 w-[36rem] h-[36rem] rounded-full blur-3xl ${
              darkMode ? "bg-cyan-500/20" : "bg-cyan-200/60"
            }`}
          />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10"
          >
            <h1 className="text-4xl sm:text-5xl font-extrabold">
              Selected Projects
            </h1>
            
            
          </motion.div>
        </section>

        {/* Filters */}
        <section className="mt-10">
          <div
            className={`rounded-2xl p-4 border ${
              darkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"
            }`}
          >
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className={`${darkMode ? "text-white/70" : "text-gray-600"}`}>
                Filter:
              </span>
              {["All", "Security", "RAG", "DevOps", "Evaluation"].map(
                (t, i) => (
                  <Chip key={t} darkMode={darkMode} active={i === 0}>
                    {t}
                  </Chip>
                )
              )}
              <span className="ml-auto inline-flex items-center gap-2 opacity-80">
                <SlidersHorizontal size={16} /> Sort
              </span>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section id="grid" className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {projects.map((p) => (
            <ProjectCard key={p.t} p={p} darkMode={darkMode} />
          ))}
        </section>

        {/* CTA Banner */}
        <section className="mt-16">
          <TiltCard
            darkMode={darkMode}
            glow
            className={`${
              darkMode
                ? "from-indigo-600/30 to-cyan-500/30 border-white/10 text-white"
                : "from-indigo-100 to-cyan-100 border-gray-200 text-gray-900"
            } rounded-3xl p-8 bg-gradient-to-r border`}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 justify-between">
              <div>
                <h3 className="text-xl font-bold">Want a deeper case study?</h3>
                <p
                  className={`${darkMode ? "text-white/80" : "text-gray-700"} text-sm mt-1`}
                >
                  I can share sanitized architecture diagrams and evaluation
                  results under NDA.
                </p>
              </div>
              <Magnetic>
                <ButtonPrimary darkMode={darkMode} href="/contact">
                  Request a private brief <ArrowRight size={16} />
                </ButtonPrimary>
              </Magnetic>
            </div>
          </TiltCard>
        </section>
      </main>

      <footer
        className={`py-10 text-sm ${
          darkMode ? "text-white/60" : "text-gray-600"
        } border-t ${darkMode ? "border-white/10" : "border-gray-200"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          © {new Date().getFullYear()} Sikandar Tareen
        </div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${
            darkMode
              ? "border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60"
              : "border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500"
          }`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? "Light Mode" : "Dark Mode"}
        </button>
      </div>

      {/* Reduce motion fallback */}
      <style>{`
        @media (prefers-reduced-motion: reduce){ *{ animation:none !important; transition:none !important } }
      `}</style>
    </div>
  );
}

/* -------------------- Data -------------------- */
const projects = [
  {
    t: "Autonomous Incident Responder",
    s: "Security • SOC",
    icon: ShieldCheck,
    bullets: [
      "Automated triage & playbooks",
      "Human approvals",
      "Evidence capture",
    ],
    meta: ["94% task success", "p95 1.7s", "≤2% interventions"],
  },
  {
    t: "Research Copilot",
    s: "Knowledge • RAG",
    icon: Cpu,
    bullets: [
      "Multi-source retrieval",
      "Citation-first answers",
      "Verification loop",
    ],
    meta: ["Freshness policy", "Citations required", "Eval harness"],
  },
  {
    t: "Conversational Cloud Ops",
    s: "DevOps • IaC",
    icon: Workflow,
    bullets: ["Safe tool use", "Rollback automation", "Policy checks"],
    meta: ["Approvals / SoD", "Blue/green", "Traces & metrics"],
  },
  {
    t: "Agentic Evaluation Suite",
    s: "Evaluation",
    icon: ShieldCheck,
    bullets: ["Scenario generation", "Safety scoring", "Dashboards"],
    meta: ["Guardrails", "Cost caps", "Alerts"],
  },
  {
    t: "Sales Enablement Agent",
    s: "RAG",
    icon: Cpu,
    bullets: ["CRM integration", "RFP answers", "Source-backed"],
    meta: ["Latency budgets", "Feedback loops", "A/B prompts"],
  },
  {
    t: "Support Auto-Triage",
    s: "Ops",
    icon: Workflow,
    bullets: ["Queue routing", "Knowledge diffing", "Escalation logic"],
    meta: ["SLO aware", "Rollback", "Audit trail"],
  },
];

/* -------------------- Components -------------------- */
function ButtonPrimary({
  children,
  href = "#",
  darkMode,
  small = false,
}) {
  return (
    <a
      href={href}
      className={`inline-flex items-center gap-2 ${
        small ? "px-3 py-1.5 text-sm" : "px-5 py-3"
      } rounded-xl font-medium bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${
        darkMode ? "focus-visible:ring-cyan-300/60" : "focus-visible:ring-indigo-500"
      }`}
    >
      {children}
    </a>
  );
}

function Chip({
  children,
  darkMode,
  active = false,
}) {
  if (active) {
    return (
      <span className={`px-3 py-1.5 rounded-full text-sm font-medium bg-blue-600 text-white`}>
        {children}
      </span>
    );
  }
  return (
    <button
      className={`px-3 py-1.5 rounded-full border text-sm transition ${
        darkMode
          ? "border-white/15 bg-white/10 text-white hover:bg-white/15"
          : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

function ProjectCard({ p, darkMode }) {
  const Icon = p.icon;
  return (
    <TiltCard
      darkMode={darkMode}
      glow
      className={`${
        darkMode
          ? "bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30"
          : "bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200"
      } rounded-2xl p-5 border`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`${darkMode ? "text-white/60" : "text-gray-600"} text-xs`}>
            {p.s}
          </div>
          <h3 className="mt-1 font-semibold text-lg">{p.t}</h3>
        </div>
        <div
          className={`$${darkMode ? "bg-white/10 text-white" : "bg-indigo-50 text-indigo-700"} p-2 rounded-lg`}
        >
          <Icon size={18} />
        </div>
      </div>
      <ul className={`${darkMode ? "text-white/80" : "text-gray-700"} mt-3 text-sm space-y-1`}>
        {p.bullets.map((b) => (
          <li key={b}>• {b}</li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs opacity-80">
        {p.meta.map((m) => (
          <span
            key={m}
            className={`${
              darkMode ? "bg-white/10 border-white/15" : "bg-gray-50 border-gray-200"
            } rounded-full border px-2 py-1`}
          >
            {m}
          </span>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Magnetic>
          <ButtonPrimary darkMode={darkMode} href="/contact">
            Build something like this <ArrowRight size={14} />
          </ButtonPrimary>
        </Magnetic>
        <a
          href="#"
          className={`${
            darkMode ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-black"
          } inline-flex items-center gap-1 text-sm`}
        >
          <Github size={14} /> Code (private)
        </a>
        <a
          href="#"
          className={`${
            darkMode ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-black"
          } inline-flex items-center gap-1 text-sm`}
        >
          <LinkIcon size={14} /> Case study
        </a>
      </div>
    </TiltCard>
  );
}

// Tilt + Magnetic helpers (same as Hire Me page)
function TiltCard({
  children,
  className = "",
  glow = false,
  darkMode = false,
}) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tx = useSpring(rx, { stiffness: 150, damping: 15 });
  const ty = useSpring(ry, { stiffness: 150, damping: 15 });
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const ROT = 8;
    rx.set((0.5 - py) * ROT);
    ry.set((px - 0.5) * ROT);
    el.style.setProperty("--mx", `${Math.round(px * 100)}%`);
    el.style.setProperty("--my", `${Math.round(py * 100)}%`);
  }
  function onLeave() {
    rx.set(0);
    ry.set(0);
  }
  const glowColor = darkMode
    ? "rgba(34,211,238,0.28)"
    : "rgba(99,102,241,0.25)";
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: tx, rotateY: ty, transformStyle: "preserve-3d" }}
      className={`group relative will-change-transform transition-shadow hover:-translate-y-0.5 ${className}`}
    >
      {glow && (
        <div
          className="pointer-events-none absolute inset-0 rounded-[inherit] blur-2xl opacity-0 group-hover:opacity-40 transition-opacity"
          style={{
            background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${glowColor}, transparent 45%)`,
          }}
        />
      )}
      {children}
    </motion.div>
  );
}
function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 120, damping: 10 });
  const sy = useSpring(y, { stiffness: 120, damping: 10 });
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    x.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    y.set(((e.clientY - r.top) / r.height - 0.5) * 10);
  }
  function onLeave() {
    x.set(0);
    y.set(0);
  }
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

function ParallaxField({ darkMode }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div
        className={`absolute left-1/4 top-10 w-40 h-40 rounded-full ${
          darkMode ? "bg-white/5" : "bg-indigo-100"
        } blur-3xl animate-[float_10s_ease-in-out_infinite]`}
      />
      <div
        className={`absolute right-10 top-1/3 w-24 h-24 rounded-full ${
          darkMode ? "bg-indigo-600/20" : "bg-indigo-200/70"
        } blur-2xl animate-[float_12s_ease-in-out_infinite]`}
      />
      <div
        className={`absolute left-10 bottom-10 w-24 h-24 rounded-full ${
          darkMode ? "bg-cyan-500/20" : "bg-cyan-200/70"
        } blur-2xl animate-[float_14s_ease-in-out_infinite]`}
      />
      <style>{`@keyframes float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-12px)} }`}</style>
    </div>
  );
}
